import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Box, Card, CardContent, CircularProgress, Slider, Stack, Switch,
  Typography,
} from '@mui/material'
import ShieldMoonOutlinedIcon from '@mui/icons-material/ShieldMoonOutlined'
import { ApiError } from '@cmms/api-client'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'

/// Range of allowed radii (in metres). 20m is the smallest that reliably
/// accounts for GPS noise on a smartphone; 1000m covers big campuses.
/// Slider snaps to 20m increments so users don't accidentally produce
/// 137m values.
const MIN_M = 20
const MAX_M = 1000
const STEP_M = 20

/// Admin tab: toggle the WO geofence + adjust the radius for the org.
/// Mobile clients (full + lite) read these prefs and gate WO actions
/// when on. Web doesn't enforce anything on its own — desktops rarely
/// have GPS, so the tab is configuration-only.
export function GeofenceTab() {
  const qc = useQueryClient()
  const orgQ = useQuery({ queryKey: ['org'], queryFn: () => api.orgs.getCurrent() })
  const org = orgQ.data

  const [enabled, setEnabled] = useState(false)
  const [radius, setRadius] = useState(100)

  useEffect(() => {
    if (!org) return
    setEnabled(org.preferences?.wo_geofence_enabled ?? false)
    setRadius(org.preferences?.wo_geofence_radius_m ?? 100)
  }, [org])

  const saveM = useMutation({
    mutationFn: (next: { wo_geofence_enabled: boolean; wo_geofence_radius_m: number }) =>
      // Merge — backend overlays this onto the existing prefs jsonb so
      // we only ship the keys we want to change. Other prefs (currency,
      // language, modulePrefixes) stay put.
      api.orgs.update({ preferences: { ...org?.preferences, ...next } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org'] }),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save'),
  })

  // Debounce: the slider can fire dozens of onChange events per drag,
  // but we don't want a PATCH for each one. Schedule a trailing-edge
  // save 500ms after the last movement; onChangeCommitted flushes
  // immediately (mouse-up / keyboard release).
  const debounceRef = useRef<number | null>(null)
  const scheduleSave = (next: { wo_geofence_enabled: boolean; wo_geofence_radius_m: number }, immediate = false) => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    if (immediate) { saveM.mutate(next); return }
    debounceRef.current = window.setTimeout(() => saveM.mutate(next), 500)
  }

  const handleToggle = (value: boolean) => {
    setEnabled(value)
    scheduleSave({ wo_geofence_enabled: value, wo_geofence_radius_m: radius }, true)
  }

  const handleSliderChange = (value: number) => {
    const snapped = Math.max(MIN_M, Math.min(MAX_M, Math.round(value / STEP_M) * STEP_M))
    setRadius(snapped)
    scheduleSave({ wo_geofence_enabled: enabled, wo_geofence_radius_m: snapped })
  }

  const handleSliderCommit = (value: number) => {
    const snapped = Math.max(MIN_M, Math.min(MAX_M, Math.round(value / STEP_M) * STEP_M))
    setRadius(snapped)
    scheduleSave({ wo_geofence_enabled: enabled, wo_geofence_radius_m: snapped }, true)
  }

  if (orgQ.isLoading) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
      <Card>
        <CardSectionHeader
          title="On-site geofence"
          subtitle="Restrict mobile WO actions to on-site technicians."
        />
        <CardContent sx={{ pt: 0, px: 3.5, pb: 3.5 }}>
          <Stack spacing={2.5}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: (t) => `${t.palette.primary.main}18`, color: 'primary.main', flexShrink: 0 }}>
                <ShieldMoonOutlinedIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }}>
                  Require on-site location
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.45, mt: 0.25 }}>
                  When on, the mobile apps block status changes and checklist execution
                  if a tech's GPS is outside the configured radius of the work order's site.
                </Typography>
              </Box>
              <Switch
                checked={enabled}
                onChange={(e) => handleToggle(e.target.checked)}
                disabled={saveM.isPending}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ opacity: enabled ? 1 : 0.55, pointerEvents: enabled ? 'auto' : 'none', transition: 'opacity 180ms ease' }}>
        <CardSectionHeader
          title="Radius"
          subtitle="How close to the site counts as on-site."
        />
        <CardContent sx={{ pt: 0, px: 3.5, pb: 3.5 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Current
              </Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'primary.main', letterSpacing: '-0.02em' }}>
                {radius} m
              </Typography>
            </Stack>
            <Slider
              value={radius}
              min={MIN_M}
              max={MAX_M}
              step={STEP_M}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v} m`}
              onChange={(_, v) => handleSliderChange(v as number)}
              onChangeCommitted={(_, v) => handleSliderCommit(v as number)}
              sx={{ mt: 0.5 }}
            />
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{MIN_M} m</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{MAX_M} m</Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}

function CardSectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Box sx={{ px: 3.5, pt: 3, pb: 2.5 }}>
      <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.015em' }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.35 }}>{subtitle}</Typography>}
    </Box>
  )
}
